import React from 'react'

/* eslint-disable no-throw-literal */
import { createAction } from 'redux-actions'
import { types } from 'features/task/store/types'
import get from 'lodash/get'
import { findPositionById, findById } from 'features/list/utils'
import { translations, utils } from 'gipsy-misc'
import { Snackbar } from 'gipsy-ui'

import { openPopup, closePopup } from 'features/popup/store/actions'
import ReviewInputLinkForm from 'features/popup/components/reviewInputLinkForm'
import { highlightElements, scrollAndHighlightElementWithId, handleAPIError } from 'features/app/store/actions'
import { closePanel, setPanelItem } from 'features/taskPanel/store/actions'
import { progressPending, progressSuccess, progressError } from 'features/progressBar/store/actions'
import { getTodoList, getArchiveList, setTodoItemsForGroup } from 'features/todolist/store/actions'
import { getTeamList, getTeamMembers, getArchiveTeamList, setTeamItemsForGroup } from 'features/teamlist/store/actions'
import { markTaskAsRead } from 'features/notifications/store/actions'
import { getCurrentPathName, pushUserToHistory } from 'utils/location'

const _updateTask = createAction(types.UPDATE_TASK)
const _removeTask = createAction(types.REMOVE_TASK)

/* ************************************************************* */
/* ******************** Actions - Front Only ******************* */
/* ************************************************************* */

/* redux action to completely override a task from todolist/teamlist */
export const updateTask = task => (dispatch, getState) => {
  const taskId = get(task, 'id')
  const position = findPositionById(taskId)
  if (!position) {
    console.debug(`task not found with id : ${taskId}`)
    return
  }
  position.element = task
  dispatch(_updateTask(position))

  const taskPanelId = getState().taskPanel.id

  // if task is a review task, we don't update item because
  // we would also need the task reviewed
  if (taskPanelId === taskId && !utils.task.isReviewTask(task)) {
    dispatch(
      setPanelItem({
        id: taskId,
        item: task
      })
    )
  }
}

/* redux actions to partially override a task from todolist/teamlist */
export const updateTaskPartiallyById = (id, data) => (dispatch, getState) => {
  const position = findPositionById(id)
  if (!position) {
    console.debug(`task not found with id : ${id}`)
    return
  }

  const element = {
    ...position.element,
    ...data
  }

  dispatch(
    _updateTask({
      element,
      index: position.index,
      group: position.group
    })
  )
}

/* ************************************************************* */
/* ****************** Actions - Async Request ****************** */
/* ************************************************************* */

export const createTaskRequest = (task, history, context) => async (dispatch, getState, { api }) => {
  dispatch(progressPending())

  try {
    const response = await api.task.create(task, context)

    const me = { userId: getState().session.id }
    const activeTeamMember = getState().teamlist.activeTeamMember

    dispatch(progressSuccess())
    dispatch(closePanel())

    if (response.taskActions) {
      await dispatch(handleTaskActionsForCreate(response.taskActions, me, activeTeamMember))
    }

    if (!response.taskActions || !response.taskActions.length) {
      return response
    }

    if (!history) {
      throw new Error('2nd argument invalid : history should be defined')
    }

    const to = response.taskActions[0].task.to
    const taskId = get(response, 'taskActions[0].taskId')
    const assigneeId = utils.user.getKey(to)
    /* if task is assigned to another user : redirect to assignee page */
    if (assigneeId && assigneeId !== getState().session.id) {
      /* fetch team members / list is handled by pages/teamlist/index because we need to handle route user */
      pushUserToHistory(history, taskId, to)
      return response
    }
    dispatch(getListsBasedOnLocation({ ...task, to }, { shouldFetchArchive: false }))
    return response
  } catch (err) {
    dispatch(progressError())
    if (get(err, 'data.code') === 'task.create.multipleAssignees') {
      dispatch(closePanel())
    }
    dispatch(handleAPIError(err, { task }))
    return { error: get(err, 'data.code') }
  }
}

export const createRequestReview = reviewRequest => async (dispatch, getState, { api }) => {
  dispatch(progressPending())

  try {
    await api.task.createRequestReview(reviewRequest)

    dispatch(progressSuccess())
    dispatch(closePanel())

    dispatch(getTeamMembers())

    const activeTeamMember = getState().teamlist.activeTeamMember

    await dispatch(getTeamList(activeTeamMember))

    return
  } catch (err) {
    dispatch(progressError())
    dispatch(handleAPIError(err, { reviewRequest }))
    return { error: get(err, 'data.code') }
  }
}

export const handleTaskActionsForCreate = (taskActions, me, activeTeamMember) => async (
  dispatch,
  getState,
  { api }
) => {
  // we will not act on deleted tasks
  taskActions = taskActions.filter(t => t.action !== 'deleted')
  if (!taskActions.length) {
    return
  }

  for (let taskAction of taskActions) {
    const task = taskAction.task
    let list, setListItemsForGroup
    if (!task.to || utils.user.areSameUser(task.to, me)) {
      list = 'todolist'
      setListItemsForGroup = setTodoItemsForGroup
    } else if (getCurrentPathName().includes('/team') && utils.user.areSameUser(task.to, activeTeamMember)) {
      list = 'teamlist'
      setListItemsForGroup = setTeamItemsForGroup
    } else {
      return
    }
    const group = utils.datetime.getGroupForItem(task, 'when.date')
    const newGroupItems = Object.assign({}, getState()[list].items[group])
    let newList = []
    newList = newGroupItems.list
    if (utils.datetime.isGroupUpcoming(group)) {
      newList = utils.datetime.insertAtLastIndexOfDate(task, newList)
    } else {
      newList.push(task)
    }
    await dispatch(setListItemsForGroup({ group: group, list: newList }))
  }

  /* removed await here, if the container doesnt exists (calendar page) it will wait forever */
  dispatch(scrollAndHighlightElementWithId(taskActions[0].taskId))
  // we also highlight the rest
  const toHighlight = taskActions.map(t => t.taskId)
  dispatch(highlightElements(toHighlight))
}

export const patchTaskRequest = (id, params, body) => async (dispatch, getState, { api }) => {
  if (!id) {
    dispatch(progressError())
    return dispatch(handleAPIError('Tried to update a task with id undefined', { id, params, body }))
  }

  dispatch(progressPending())
  try {
    const response = await api.task.patch(id, params, body)
    dispatch(progressSuccess())

    const fetchListParams = [
      'estimatedTime',
      'to',
      'assignee',
      'when.date',
      'pin.date',
      'pin.time',
      'recurrencyDetails'
    ]

    if (!response.taskActions || !response.taskActions.length) {
      return response
    }
    const filteredTaskActions = response.taskActions.filter(t => t.action !== 'deleted')
    const to = filteredTaskActions[0].task.to

    const pathName = getCurrentPathName()

    if (utils.object.hasAtleastOneKeyInArray(params, fetchListParams)) {
      const me = { userId: getState().session.id }
      const activeTeamMember = getState().teamlist.activeTeamMember
      const isAssignedToMe = !to || utils.user.areSameUser(to, me)
      const changedAssigneeAndIsTodolistPage = (params.to || params.assignee) && pathName === '/'

      if (isAssignedToMe || changedAssigneeAndIsTodolistPage) {
        /* we dont wait for getTodoList to be done because the taskPanel doesnt close after patch */
        dispatch(getTodoList())
      } else if (pathName.includes('/team')) {
        dispatch(getTeamMembers())
        if (pathName === '/team' && (utils.user.areSameUser(to, activeTeamMember) || params.assignee)) {
          await dispatch(getTeamList(activeTeamMember))
        }
      }

      // we only highlight if the task was potentially moved
      if (filteredTaskActions.length > 0) {
        const toHighlight = filteredTaskActions.map(t => t.taskId)
        dispatch(highlightElements(toHighlight))
      }
      setTimeout(() => {
        dispatch(highlightElements())
      }, 5 * 1000)
    }

    if (response.newCommentId) {
      utils.DOM.scrollWhenExistsToElementWithId(response.newCommentId, 'panel-scrollable')
    }

    return response
  } catch (err) {
    dispatch(progressError())
    const error = dispatch(handleAPIError(err, { id, params }))
    throw { error }
  }
}

export const toggleCompleteTaskRequest = (id, value, context) => async (dispatch, getState, { api }) => {
  if (!id) {
    dispatch(progressError())
    dispatch(handleAPIError('Tried to toggle a task with id undefined', { id, value }))
    return
  }
  let fn = 'uncomplete'
  if (value) {
    fn = 'complete'
  }
  dispatch(markTaskAsRead(id))

  const task = Object.assign({}, findById(id))

  const shouldAskForReviewInputLink =
    fn === 'complete' && !task.reviewInputLink && task.watchers && task.watchers.filter(w => w.isReviewer).length > 0
  if (shouldAskForReviewInputLink) {
    dispatch(
      openPopup({
        logo: 'lookdown',
        message: translations.task.reviewInputLink.popup.message,
        button: translations.task.reviewInputLink.popup.provideLink,
        component: (
          <ReviewInputLinkForm
            key={id} // we need to pass a key to remount a new component
            onProvideLink={reviewInputLink => dispatch(onProvideLink(task, fn, id, value, context, reviewInputLink))}
            onSkip={() => dispatch(onSkipLink(task, fn, id, value, context))}
          />
        ),
        onClose: () => dispatch(onSkipLink(task, fn, id, value, context))
      })
    )
    return
  }

  dispatch(completeFlow(task, fn, id, value, context))
}

const onProvideLink = (task, fn, id, value, context, reviewInputLink) => async (dispatch, getState, { api }) => {
  dispatch(closePopup())
  dispatch(completeFlow(task, fn, id, value, context, { reviewInputLink }))
}

const onSkipLink = (task, fn, id, value, context) => async (dispatch, getState, { api }) => {
  dispatch(closePopup())
  dispatch(completeFlow(task, fn, id, value, context))
}

const completeFlow = (task, fn, id, value, context, extraParams) => async (dispatch, getState, { api }) => {
  dispatch(progressPending())
  try {
    const data = await api.task[fn](id, context, extraParams)

    dispatch(progressSuccess())
    if (fn !== 'complete') {
      return
    }
    const sessionUser = getState().session.user
    const displayName = sessionUser.displayName || ''

    if (data.completedOnboarding) {
      dispatch(
        openPopup({
          title: get(translations, `onboarding.tasks.completedOnboardingPopup.title`).replace(
            '[displayName]',
            displayName
          ),
          message: get(translations, `onboarding.tasks.completedOnboardingPopup.text`),
          button: get(translations, `onboarding.tasks.completedOnboardingPopup.buttonText`),
          action: 'onClose',
          logo: 'happy'
        })
      )
    } else {
      const message = utils.task.getRandomCompletionMessage(task, sessionUser)
      dispatch(
        Snackbar.show(
          {
            message: message,
            status: 'default',
            showLogo: true,
            showClose: true
          },
          5 * 1000
        )
      )
    }

    dispatch(getListsBasedOnLocation(task))
  } catch (err) {
    dispatch(progressError())
    dispatch(handleAPIError(err, { id, value }))
  }
}

export const getAndUpdateTask = taskId => async (dispatch, getState, { api }) => {
  let task = await api.task.get(taskId)
  if (!task || !task.id) {
    return
  }

  // if the current task has an ETA, let's not overwrite it
  const position = findPositionById(taskId)
  if (!position) {
    console.debug(`task not found with id : ${taskId}`)
    return
  }
  if (position.element.ETA) {
    task.ETA = position.element.ETA
  }

  dispatch(updateTask(task))
}

export const removeTask = taskId => async (dispatch, getState, { api }) => {
  const position = findPositionById(taskId)
  if (!position) {
    console.debug(`task not found with id : ${taskId}`)
    return
  }
  position.taskId = taskId
  dispatch(_removeTask(position))
}

export const deleteTaskRequest = (taskId, { deleteMode }) => async (dispatch, getState, { api }) => {
  try {
    const task = Object.assign({}, findById(taskId))
    const response = await api.task.del(taskId, deleteMode)
    dispatch(removeTask(taskId))
    dispatch(progressSuccess())
    /* in case the user opened the task panel with another task before the request is complete */
    if (getState().taskPanel.item && getState().taskPanel.item.id === taskId) {
      dispatch(closePanel())
    }

    dispatch(
      getListsBasedOnLocation(task, {
        shouldFetchArchive: false
      })
    )

    return response
  } catch (err) {
    dispatch(progressError())
    dispatch(handleAPIError(err, { taskId }))
    return { error: get(err, 'data.code') }
  }
}

export const getListsBasedOnLocation = (
  task,
  {
    shouldFetchTodoList = true,
    shouldFetchTeamMembers = true,
    shouldFetchTeamList = true,
    shouldFetchArchive = true
  } = {}
) => async (dispatch, getState, { api }) => {
  const pathName = getCurrentPathName()
  const assignee = (task.review && task.review.to) || task.to
  const me = { userId: getState().session.id }
  const activeTeamMember = getState().teamlist.activeTeamMember
  if (
    (pathName === '/' || pathName === '/archive' || pathName === '/focusview') &&
    (!assignee || utils.user.areSameUser(assignee, me))
  ) {
    if (shouldFetchArchive) {
      dispatch(getArchiveList())
    }
    if (shouldFetchTodoList) {
      await dispatch(getTodoList())
    }
  } else if (pathName.includes('/team')) {
    if (shouldFetchArchive) {
      dispatch(getArchiveTeamList(activeTeamMember))
    }
    if (shouldFetchTeamMembers) {
      dispatch(getTeamMembers())
    }
    if (shouldFetchTeamList && utils.user.areSameUser(assignee, activeTeamMember)) {
      await dispatch(getTeamList(activeTeamMember))
    }
  }
}

export const approveReview = id => async (dispatch, getState, { api }) => {
  await api.task.approveReview(id)
}

export const reopenReview = id => async (dispatch, getState, { api }) => {
  await api.task.reopenReview(id)
}



// WEBPACK FOOTER //
// ./src/features/task/store/actions.js